import { useCallback } from "react";
import { useFilterContext } from "../FilterContext";
import { useUIContext } from "../UIContext";
import { useDataContext } from "../DataContext";
import { useCatalogueUrlSync } from "./useCatalogueUrlSync";
import { catalogueCoordinator } from "../services/catalogueCoordinator";
import { buildFetchParams } from "../helpers/filterHelpers";

/**
 * Hook for managing filter operations with data fetching coordination
 * @returns {Object} Object containing filter functions
 */
export const useCatalogueFilters = () => {
  const filterContext = useFilterContext();
  const uiContext = useUIContext();
  const dataContext = useDataContext();
  const { syncUrlState } = useCatalogueUrlSync();

  /**
   * Filters learning activities based on search input and categories
   * Uses client-side filtering, no API call
   * @param {Event} event - Form event (optional)
   * @param {string} searchInput - Search text to filter courses
   * @param {Object} updatedCategoriesObject - Object containing category filter values
   */
  const filterLearnings = useCallback(
    async (event, searchInput, updatedCategoriesObject) => {
      if (event) {
        event.preventDefault();
      }

      filterContext.updateFilters({
        searchText: searchInput,
        categoriesValue: updatedCategoriesObject,
      });
      uiContext.setPage(1);

      const params = buildFetchParams(
        {
          ...filterContext.filters,
          searchText: searchInput,
          categoriesValue: updatedCategoriesObject,
        },
        1,
      );
      await dataContext.applyFiltersToData(params);
    },
    [filterContext, uiContext, dataContext],
  );

  /**
   * Sets the sort order and filters client-side
   * @param {number} sortOrder - Sort order parameter
   */
  const setSortOrder = useCallback(
    async (sortOrder) => {
      filterContext.updateFilters({ sortOrder });
      const next = { ...filterContext.filters, sortOrder };
      const params = buildFetchParams(next, uiContext.uiState.page);
      await dataContext.applyFiltersToData(params);
      syncUrlState(next, uiContext.uiState);
    },
    [filterContext, uiContext, dataContext, syncUrlState],
  );

  /**
   * Sets the search text and filters client-side
   * @param {string} searchText - Search text to filter courses
   */
  const setSearchText = useCallback(
    async (searchText) => {
      filterContext.updateFilters({ searchText });
      uiContext.setPage(1);
      const next = { ...filterContext.filters, searchText };
      const nextUi = { ...uiContext.uiState, page: 1 };
      const params = buildFetchParams(next, 1);
      await dataContext.applyFiltersToData(params);
      syncUrlState(next, nextUi);
    },
    [filterContext, uiContext, dataContext, syncUrlState],
  );

  /**
   * Sets the category values and filters client-side
   * @param {Object} categoriesValue - Object containing category filter values
   */
  const setCategoriesValue = useCallback(
    async (categoriesValue) => {
      filterContext.updateFilters({ categoriesValue });
      uiContext.setPage(1);
      const next = { ...filterContext.filters, categoriesValue };
      const nextUi = { ...uiContext.uiState, page: 1 };
      const params = buildFetchParams(next, 1);
      await dataContext.applyFiltersToData(params);
      syncUrlState(next, nextUi);
    },
    [filterContext, uiContext, dataContext, syncUrlState],
  );

  /**
   * Resets all filters to default values and re-filters existing data
   * No API call needed - just re-filter cached data
   */
  const resetFilters = useCallback(async () => {
    filterContext.resetFilters();
    uiContext.setPage(1);
    const params = {
      searchInput: "",
      category1: 0,
      category2: 0,
      category3: 0,
      category4: 0,
      category5: 0,
      pageNum: 1,
      sortOrderParam: 0,
      startDate: null,
      endDate: null,
      sessionRegion: "",
      sessionCity: "",
    };
    await dataContext.applyFiltersToData(params);
    const next = {
      ...filterContext.filters,
      searchText: "",
      categoriesValue: {
        category1: 0,
        category2: 0,
        category3: 0,
        category4: 0,
        category5: 0,
      },
      sortOrder: 0,
      startDate: "",
      endDate: "",
      sessionRegion: "",
      sessionCity: "",
    };
    const nextUi = { ...uiContext.uiState, page: 1 };
    syncUrlState(next, nextUi);
  }, [filterContext, uiContext, dataContext, syncUrlState]);

  /**
   * Updates filters with optional refetch based on filter type
   * Only refetches if date/session filters change (server-side filters)
   * Otherwise uses client-side filtering
   * @param {Object} updates - Filter updates to apply
   */
  const updateFilters = useCallback(
    async (updates) => {
      await catalogueCoordinator.updateFiltersWithRefetch({
        filterContext,
        uiContext,
        dataContext,
        syncUrlState,
        updates,
      });
    },
    [filterContext, uiContext, dataContext, syncUrlState],
  );

  return {
    filterLearnings,
    setSortOrder,
    setSearchText,
    setCategoriesValue,
    resetFilters,
    updateFilters,
  };
};
